import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';

import { ActionStep, ActionStepTemplate } from 'types';

import { postNewGoalAction, editGoalAction } from 'api/goal_actions';

import ActionStepCard from './Actions/components/ActionStepCard';
import ActionStepForm from './Actions/components/ActionStepForm';
import ActionStepSummary from './Actions/components/ActionStepSummary';
import AddBorder from 'assets/border-add.svg';
import BonusTrackerCard from './Actions/components/BonusTrackerCard';
import ExtraBonusTrackerCard from './Actions/components/ExtraBonusTrackerCard';
import CardBackground from 'components/_shared/CardBackground';
import LoadingCircle from 'components/_shared/LoadingCircle';
import MessageBubble from 'components/_shared/MessageBubble';
import Modal2 from 'components/_shared/Modal2';

import useMemberInfo from 'hooks/useMemberInfo';
import useRemoteCopy from 'hooks/useRemoteCopy';
import useTemplateActions from 'hooks/useActionTemplate';

import { Button, Fade } from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    stepsContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#2F365B',
        borderRadius: '16px',
        height: 'fit-content',
        padding: '50px 0 0 0',
        width: '100%'
    },
    statusTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        fontWeight: 600,
        color: '#FFFFFF',
        lineHeight: '26px',
        marginBottom: '30px',
        position: 'relative',
        zIndex: 1
    },
    needContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '20px',
        maxWidth: '100%',
        minHeight: '50%',
        minWidth: '85%',
        padding: '0 40px',
        zIndex: 1
    },
    fadeContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '20px 0',
        width: '100%'
    },
    addContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${AddBorder})`,
        backgroundColor: '#EAEFF3',
        borderRadius: '40px',
        minHeight: '300px',
        width: '100%',
        '&:active': {
            transform: 'scale(0.98)'
        }
    },
    addButton: {
        animation: '$pulse 2.5s infinite',
        background: 'linear-gradient(180deg, #CCD3FF 0%, #7771B4 100%)',
        backgroundColor: '#9A9EE7',
        border: 'none',
        borderRadius: '100%',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '40px',
        height: '60px',
        width: '60px'
    },
    addButtonContent: {
        color: '#7E72D4',
        font: 'Manrope',
        fontWeight: '800',
        letterSpacing: '0.15em',
        lineHeight: '40px',
        padding: '10px',
        size: '40px',
        textAlign: 'center'
    },
    noTooltip: {
        margin: '40px',
        width: '95%'
    },
    tooltipContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#FEF3E7',
        borderRadius: '20px 20px 40px 40px',
        color: '#7A4406',
        margin: '10px 0',
        width: '95%'
    },
    bonusTooltip: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '16px 32px 24px 32px'
    },
    tooltipHeader: {
        fontSize: '16px',
        fontWeight: '800',
        lineHeight: '22px',
        '& i': {
            marginRight: '8px'
        }
    },
    tooltipBody: {
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '22px'
    },
    btnContainer: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        zIndex: 1,
        margin: '48px 0 12px 0',
    },
    doneBtn: {
        display: 'flex',
        alignItems: 'center',
        borderRadius: '100px',
        fontSize: '28px',
        fontWeight: '700',
        padding: '12px 72px',
        textTransform: 'none',
        background: '#7378E8',
        color: '#FFFFFF',
        '& i': {
            fontSize: '40px',
            marginLeft: '15px'
        }
    },
    cancelBtn: {
        display: 'flex',
        fontSize: '20px',
        fontWeight: '700',
        color: '#7378E8',
        padding: '5px 20px',
        borderRadius: '36px',
        textTransform: 'none'
    },
    '@keyframes pulse': {
        '0%': {
            boxShadow: '0 0 0 0 rgba(154, 158, 231, 0.4)'
        },
        '70%': {
            boxShadow: '0 0 0 10px rgba(154, 158, 231, 0)'
        },
        '100%': {
            boxShadow: '0 0 0 0 rgba(154, 158, 231, 0)'
        }
    }
}));

type CreateActionStepsPopupProps = {
    holderId: number; 
    orgId?: number;
    setAmountOfActionSteps: (amount: number) => void;
    handleClose: () => void;
};

const CreateActionStepsPopup: React.FC<CreateActionStepsPopupProps> = ({
    holderId,
    orgId,
    setAmountOfActionSteps,
    handleClose
}) => {
    const classes = useStyles();

    const actions = useRemoteCopy('action_step_suggestions');
    const copy = useRemoteCopy('intake');
    const member = useMemberInfo(holderId);
    const messageText = useMemo(() => copy.remoteCopy?.pages.add_action_step.bubble.replace(/_([^_]*)_/g, '<i>$1</i>') || 'What\'s an action step that _we can_ take here?', [copy]);
    const { templateActions, isLoaded: templateActionsLoaded } =
        useTemplateActions(orgId);
    const allLoaded = actions.isLoaded && copy.isLoaded && member.isLoaded && templateActionsLoaded;
    const anyError = actions.isError || copy.isError || member.isError;

    useEffect(() => {
        if (anyError) handleClose();
    }, [anyError, handleClose]);

    // Indices of rows in "edit" mode in the summary (user tapped edit on that card).
    const [editAction, setEditAction] = useState<number[]>([]);
    // Local list of action steps for this intake session. Each item may gain `_serverData`
    // after a successful background create/edit (API record).
    const [intakeGoals, setIntakeGoals] = useState<any[]>([]);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showForm, setShowForm] = useState(false);
    // Draft row shown above the list / in the card animation before it is merged into `intakeGoals`.
    const [temporaryAction, setTemporaryAction] = useState<
        ActionStep | ActionStepTemplate | null
    >(null);

    // is currently submitting a request to the API on the background.
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Per-index error messages from failed background saves; also drives reopening edit for retry.
    const [failedIndices, setFailedIndices] = useState<Map<number, string>>(new Map());
    const [sparkleIndex, setSparkleIndex] = useState<number | null>(null);
    const sparkleLoopRef = useRef(0);

    // Mirror latest state into refs so `handleIntakeGoalsUpdate` always reads current goals/failures
    // without listing them in useCallback deps (avoids stale closures and callback churn).
    const intakeGoalsRef = useRef(intakeGoals);
    intakeGoalsRef.current = intakeGoals;
    const failedIndicesRef = useRef(failedIndices);
    failedIndicesRef.current = failedIndices;

    const cardStaticStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        width: '100%'
    };

    const cardSpring = useSpring({
        config: { tension: 80, friction: 15 },
        delay: temporaryAction ? 1000 : 0,
        from: { opacity: 1, y: 0 },
        to: { opacity: 1, y: temporaryAction ? -137 : 0 },
        onRest: () => {
            // After the card slide animation finishes, commit the draft into `intakeGoals` and
            // kick off `backgroundCreate` for that new index (see `setTemporaryActionIntoGoals`).
            if (temporaryAction) {
                setSparkleIndex(intakeGoals.length);
                sparkleLoopRef.current = 0;
                setTemporaryActionIntoGoals();
                if (showForm) {
                    setShowForm(false);
                }
            }
        }
    });

    const animatedStyle = {
        ...cardStaticStyle,
        opacity: cardSpring.opacity,
        transform: cardSpring.y.to((y) => `translateY(${y || 0}px)`)
    };

    const getActionBonusAmount = (action: {
        default?: boolean;
        amount?: number;
        bonus?: number;
    }) => {
        if (action.default === true) return 0;

        let amount = 0;
        if (action.amount) amount += Number(action.amount);
        if (action.bonus) amount += Number(action.bonus);
        return amount;
    };

    const isExtraPoolAction = (action: any) =>
        action?.classification === 'capital_foundation_pool' ||
        action?._serverData?.classification === 'capital_foundation_pool';

    const getBonusPoolAmounts = () => {
        const baseLimit =
            member.memberInfo?.action_step_bonus.base_limit || 0;
        const serverBaseUsed =
            member.memberInfo?.action_step_bonus.base_used || 0;
        const scfLimit = member.memberInfo?.action_step_bonus.scf_limit ?? 0;
        const serverScfUsed =
            member.memberInfo?.action_step_bonus.scf_used ?? 0;

        let localBaseUsed = 0;
        let localScfUsed = 0;

        intakeGoals.forEach((goal: any) => {
            const amount = getActionBonusAmount(goal);
            if (isExtraPoolAction(goal)) {
                localScfUsed += amount;
            } else {
                localBaseUsed += amount;
            }
        });

        if (temporaryAction) {
            const amount = getActionBonusAmount(temporaryAction as any);
            if (isExtraPoolAction(temporaryAction)) {
                localScfUsed += amount;
            } else {
                localBaseUsed += amount;
            }
        }

        const remainingBase = Math.max(
            0,
            baseLimit - serverBaseUsed - localBaseUsed
        );
        const remainingScf = Math.max(
            0,
            scfLimit - serverScfUsed - localScfUsed
        );

        const isExtraBonusParticipant = scfLimit > 0;
        const shouldUseExtraBonusPool =
            isExtraBonusParticipant && remainingBase === 0;
        const activeRemaining = shouldUseExtraBonusPool
            ? remainingScf
            : remainingBase;
        const totalRemaining = remainingBase + remainingScf;

        return {
            baseLimit,
            scfLimit,
            remainingBase,
            remainingScf,
            isExtraBonusParticipant,
            shouldUseExtraBonusPool,
            activeRemaining,
            totalRemaining
        };
    };

    const getAssignedBonusMessage = () => {
        const currentMonth = new Date().toLocaleString('default', {
            month: 'long'
        });
        const nextMonth = new Date(
            new Date().setMonth(new Date().getMonth() + 1)
        ).toLocaleString('default', { month: 'long' });
        const { isExtraBonusParticipant } = getBonusPoolAmounts();

        if (isExtraBonusParticipant) {
            return (
                `You've assigned all of ${currentMonth}'s bonus pools. ` +
                `You'll be able to assign bonuses again at the start of ${nextMonth}.`
            );
        }

        return (
            `You've assigned all of ${currentMonth}'s monthly bonus pool. ` +
            `You'll be able to assign bonuses again at the start of ${nextMonth}.`
        );
    };

    // Persist a new row to the API without blocking the UI. On success, attach `json.goal_action`
    // as `_serverData` on that list index; on failure, record the error and mark the row for edit/retry.
    const backgroundCreate = useCallback(async (index: number, actionData: any) => {
        setIsSubmitting(true);
        try {
            const response = await postNewGoalAction({
                description: actionData.description,
                homeless_id: holderId,
                due_at: (actionData.due_at as string) || '',
                stake_match_amount: Number(actionData.amount || actionData.bonus || 0),
                qol_measure_id: (actionData.quality_of_life_measure_id as number) || null,
                goal_id: undefined as any,
                classification:
                    actionData.classification === 'capital_foundation_pool'
                        ? 'capital_foundation_pool'
                        : undefined
            });
            if (!response.ok) throw new Error('Failed to create action step');

            const json = await response.json();
            if (json.errors) throw new Error(json.errors?.due_at?.[0] || 'Validation error');

            setIntakeGoals(prev => {
                const updated = [...prev];
                if (updated[index]) {
                    updated[index] = { ...updated[index], _serverData: json.goal_action };
                }
                return updated;
            });
            setFailedIndices(prev => {
                const next = new Map(prev);
                next.delete(index);
                return next;
            });
        } catch (error: any) {
            setFailedIndices(prev => new Map(prev).set(index, error?.message || 'Failed to save'));
            setEditAction(prev => [...new Set([...prev, index])]);
        } finally {
            setIsSubmitting(false);
        }
    }, [holderId]);

    // PATCH an existing goal_action when local form data diverges from `_serverData`. Empty diff
    // skips the request. Same success/failure bookkeeping as `backgroundCreate`.
    const backgroundEdit = useCallback(async (index: number, actionData: any, serverData: any) => {
        setIsSubmitting(true);
        try {
            const payload: any = { goal_action: {} };

            if (serverData.description !== actionData.description) {
                payload.goal_action.description = actionData.description;
            }
            if (serverData.match_value !== Number(actionData.amount || 0)) {
                payload.goal_action.stake_match_amount = Number(actionData.amount || 0);
            }
            if (serverData.due_at !== actionData.due_at) {
                payload.goal_action.due_at = actionData.due_at;
            }
            if (
                actionData.quality_of_life_measure_id &&
                serverData.quality_of_life_measures?.[0] !== actionData.quality_of_life_measure_id
            ) {
                payload.goal_action.quality_of_life_measure_id = actionData.quality_of_life_measure_id;
            }

            if (Object.keys(payload.goal_action).length === 0) return;

            const response = await editGoalAction(payload, serverData.id);
            if (!response.ok) throw new Error('Failed to update action step');

            const json = await response.json();
            if (json.errors) throw new Error('Validation error');

            setIntakeGoals(prev => {
                const updated = [...prev];
                if (updated[index]) {
                    updated[index] = { ...updated[index], _serverData: json.goal_action };
                }
                return updated;
            });
            setFailedIndices(prev => {
                const next = new Map(prev);
                next.delete(index);
                return next;
            });
        } catch (error: any) {
            setFailedIndices(prev => new Map(prev).set(index, error?.message || 'Failed to save'));
            setEditAction(prev => [...new Set([...prev, index])]);
        } finally {
            setIsSubmitting(false);
        }
    }, []);

    // Keep latest create/edit implementations reachable from a stable `handleIntakeGoalsUpdate`
    // (empty deps) so child setters don't need to change identity every render.
    const backgroundCreateRef = useRef(backgroundCreate);
    backgroundCreateRef.current = backgroundCreate;
    const backgroundEditRef = useRef(backgroundEdit);
    backgroundEditRef.current = backgroundEdit;

    // Called when ActionStepForm/Summary updates the list. We merge with refs so we can:
    // - If the row was already saved but the child dropped `_serverData`, re-attach it and fire
    //   `backgroundEdit` when the edited fields differ from the server snapshot.
    // - If this index previously failed create and still has no `_serverData`, retry `backgroundCreate`.
    const handleIntakeGoalsUpdate = useCallback((newGoals: any[]) => {
        const currentGoals = intakeGoalsRef.current;
        const failed = failedIndicesRef.current;

        const preserved = newGoals.map((goal, i) => {
            if (i < currentGoals.length && currentGoals[i]?._serverData && !goal._serverData) {
                backgroundEditRef.current(i, goal, currentGoals[i]._serverData);
                return { ...goal, _serverData: currentGoals[i]._serverData };
            }
            if (failed.has(i) && !goal._serverData) {
                backgroundCreateRef.current(i, goal);
            }
            return goal;
        });

        setIntakeGoals(preserved);
    }, []);

    // Append the animated draft to the list, clear the draft, then create on the server for
    // `newIndex` (must match the index we just pushed — length before append).
    const setTemporaryActionIntoGoals = () => {
        if (temporaryAction) {
            const newIndex = intakeGoals.length;
            setIntakeGoals([...intakeGoals, temporaryAction]);
            setTemporaryAction(null);
            backgroundCreate(newIndex, temporaryAction);
        }
    };

    // Opening the add form: flush any in-flight draft into the list (and its background create) first.
    const handleAddClick = () => {
        setTemporaryActionIntoGoals();
        setEditAction([]);
        setSparkleIndex(null);
        sparkleLoopRef.current = 0;
        setShowForm(true);
    };

    // Same draft flush as `handleAddClick`, then open edit UI for that index (or merge draft row when index is the pending slot).
    const handleEditClick = (index: number) => {
        setTemporaryActionIntoGoals();
        setShowForm(false);

        if (index !== intakeGoals.length && !editAction.includes(index)) {
            setEditAction([...editAction, index]);
        }

        if (temporaryAction && index === intakeGoals.length) {
            setEditAction([...editAction, index]);
            setIntakeGoals([...intakeGoals, temporaryAction]);
            setTemporaryAction(null);
        }
    };

    const handleDoneClick = () => {
        handleClose();
    };

    const handleSparkleLoop = useCallback(() => {
        sparkleLoopRef.current += 1;
        if (sparkleLoopRef.current >= 3) {
            setSparkleIndex(null);
            sparkleLoopRef.current = 0;
        }
    }, []);

    const renderAddText = () =>
        intakeGoals.length === 0
            ? 'ADD ACTION STEP'
            : 'ADD ANOTHER ACTION STEP';

    const {
        baseLimit,
        scfLimit,
        remainingBase,
        remainingScf,
        isExtraBonusParticipant,
        shouldUseExtraBonusPool,
        activeRemaining,
        totalRemaining
    } = getBonusPoolAmounts();

    const shouldShowTooltip = totalRemaining === 0;

    useEffect(() => {
        setAmountOfActionSteps(intakeGoals.length);
    }, [intakeGoals.length, setAmountOfActionSteps]);

    return (
        <Modal2 onClose={handleClose} title="Create Action Steps">
            <div className={classes.stepsContainer}>
                <CardBackground />
                <div className={classes.statusTitle}>
                    Create Action Steps
                </div>
                <MessageBubble name={member.memberInfo?.first_name ?? 'YOUR MEMBER'} question={messageText} />
                <div className={classes.needContainer}>
                    {!allLoaded ? (
                        <LoadingCircle />
                    ) : (
                        <>
                            <BonusTrackerCard
                                maxBonusAmount={baseLimit}
                                remainingBonusAmount={remainingBase}
                            />
                            <ExtraBonusTrackerCard
                                maxBonusAmount={scfLimit}
                                remainingBonusAmount={remainingScf}
                                baseRemainingAmount={remainingBase}
                                baseLimit={baseLimit}
                            />
                            {intakeGoals.length > 0 && !showEditForm && (
                                <ActionStepSummary
                                    actionType={intakeGoals}
                                    actionSuggestions={actions.remoteCopy}
                                    bonusType={
                                        shouldUseExtraBonusPool
                                            ? 'extra'
                                            : 'base'
                                    }
                                    editAction={editAction}
                                    failedIndices={failedIndices}
                                    from="intake"
                                    handleEditClick={handleEditClick}
                                    intakeGoals={intakeGoals}
                                    isExtraBonusParticipant={
                                        isExtraBonusParticipant
                                    }
                                    onSparkleLoop={handleSparkleLoop}
                                    remainingBaseBonusAmount={remainingBase}
                                    remainingBonusAmount={activeRemaining}
                                    remainingScfBonusAmount={remainingScf}
                                    setEditAction={setEditAction}
                                    setGuide={() => {}}
                                    setIntakeGoals={handleIntakeGoalsUpdate}
                                    setShowForm={setShowForm}
                                    showForm={showForm}
                                    sparkleIndex={sparkleIndex}
                                    temporaryAction={temporaryAction}
                                />
                            )}
                            {temporaryAction &&
                                !editAction.includes(intakeGoals.length) && (
                                    <div
                                        style={{
                                            height: showForm
                                                ? '680px'
                                                : '126px',
                                            width: '100%'
                                        }}
                                    >
                                        <animated.div style={animatedStyle}>
                                            <Fade
                                                in={temporaryAction !== null}
                                                timeout={800}
                                            >
                                                <div
                                                    className={
                                                        classes.fadeContainer
                                                    }
                                                >
                                                    <ActionStepCard
                                                        action={
                                                            temporaryAction as any
                                                        }
                                                        handleEditClick={
                                                            handleEditClick
                                                        }
                                                        index={
                                                            intakeGoals.length
                                                        }
                                                        remainingBonusAmount={activeRemaining}
                                                        temporaryAction={
                                                            temporaryAction
                                                        }
                                                    />
                                                </div>
                                            </Fade>
                                        </animated.div>
                                    </div>
                                )}
                            {!showForm && (
                                <Fade in={true} timeout={1000}>
                                <div
                                    className={
                                        shouldShowTooltip
                                            ? classes.tooltipContainer
                                            : classes.noTooltip
                                    }
                                >
                                    {shouldShowTooltip && (
                                        <div className={classes.bonusTooltip}>
                                            <span
                                                className={
                                                    classes.tooltipHeader
                                                }
                                            >
                                                <i className="fas fa-info-circle" />{' '}
                                                Heads Up!
                                            </span>
                                            <span
                                                className={classes.tooltipBody}
                                            >
                                                {getAssignedBonusMessage()}
                                            </span>
                                        </div>
                                    )}
                                    <div className={classes.addContainer}>
                                        <button
                                            className={classes.addButton}
                                            onClick={() => handleAddClick()}
                                        >
                                            +
                                        </button>
                                        <span
                                            className={
                                                classes.addButtonContent
                                            }
                                        >
                                            {renderAddText()}
                                        </span>
                                    </div>
                                </div>
                                </Fade>
                            )}
                            {showForm && (
                                <Fade in={showForm} timeout={1000}>
                                    <div style={{ width: '97.5%' }}>
                                        <ActionStepForm
                                            actionSuggestions={
                                                actions.remoteCopy
                                            }
                                            bonusType={
                                                shouldUseExtraBonusPool
                                                    ? 'extra'
                                                    : 'base'
                                            }
                                            editAction={editAction}
                                            from="intake"
                                            intakeGoals={intakeGoals}
                                            isExtraBonusParticipant={
                                                isExtraBonusParticipant
                                            }
                                            remainingBonusAmount={activeRemaining}
                                            setGuide={() => {}}
                                            setIntakeGoals={handleIntakeGoalsUpdate}
                                            setShowEdit={setShowEditForm}
                                            setShowForm={setShowForm}
                                            setTemporaryAction={setTemporaryAction}
                                            showEdit={showEditForm}
                                            templateActions={templateActions}
                                            temporaryAction={temporaryAction}
                                        />
                                    </div>
                                </Fade>
                            )}
                        </>
                    )}
                </div>
                <div className={classes.btnContainer}>
                    {intakeGoals.length === 0 || isSubmitting ? (
                        null
                    ) : (
                        <Button
                            className={classes.doneBtn}
                            onClick={handleDoneClick}
                        >
                            Done
                        </Button>
                    )}
                </div>
            </div>
        </Modal2>
    );
};

export default CreateActionStepsPopup;
